import { Alert, Box, Link as MuiLink, Skeleton, Typography } from '@mui/material';
import { useEffect, useMemo, useRef } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { neutral } from '@ahora-patagonia/design-tokens';
import { EspacioPublicitario } from '../../../shared/ads/EspacioPublicitario.js';
import { TiraLateral } from '../../../shared/lateral/TiraLateral.js';
import { SECCION_KIND } from '../../../seccion/models/entities/SeccionData.js';
import { formatearFechaYHora } from '../../../shared/formato/fecha.js';
import { ImagenNota } from '../../../shared/media/ImagenNota.js';
import { SIZES } from '../../../shared/media/srcset.js';
import { useObservable } from '../../../shared/hooks/useObservable.js';
import { useViewModel } from '../../../shared/hooks/useViewModel.js';
import { useSincronizarPagina } from '../../../shared/shell/paginaPublica.js';
import { useSenalDePagina } from '../../../shared/senales/useSenalDePagina.js';
import { useSitio, useSsrData } from '../../../shared/ssr/SsrDataProvider.js';
import { NotFoundView } from '../../../shared/views/NotFoundView.js';
import type { NotFoundData } from '../../../notfound/notfound.loader.js';
import { useHead } from '../../../shared/seo/useHead.js';
import { headDeNota } from '../../nota.head.js';
import { NOTA_ROUTE_KEY } from '../../nota.loader.js';
import { useSenalesDeNota } from '../../../shared/senales/useSenalesDeNota.js';
import type { NotaData } from '../../models/entities/NotaData.js';
import { NotaViewModel } from '../../viewmodels/NotaViewModel.js';
import { BotonesCompartir } from '../components/BotonesCompartir.js';
import { MasDeCategoria } from '../components/MasDeCategoria.js';
import { RendererCuerpo } from '../components/cuerpo/RendererCuerpo.js';

/**
 * La nota — la pantalla más visitada del sitio: el lector llega directo de WhatsApp o
 * Google, así que presenta el medio arriba con la cabecera completa y ofrece salida por
 * categoría y por búsqueda.
 *
 * La columna de lectura va **centrada**, con un máximo de 880 px; autor y fecha van arriba,
 * entre la bajada y la foto, separados por reglas de 1 px, con compartir a la derecha y
 * repetido al final. Nada flota.
 */
export function NotaView() {
  const params = useParams();
  const sitio = useSitio();
  const initial = useSsrData<NotaData | NotFoundData>(NOTA_ROUTE_KEY);
  const esNota = initial !== null && 'nota' in initial;
  const year = params['year'] ?? '';
  const month = params['month'] ?? '';
  const urlKey = params['urlKey'] ?? '';
  const vm = useViewModel(
    () => new NotaViewModel(esNota ? initial : null, { year, month, urlKey }),
  );
  const state = useObservable(vm.state$);
  /** La dirección que resolvió el server. Los datos del SSR valen solo para esa. */
  const direccionDelSsr = useRef(`${year}/${month}/${urlKey}`).current;

  // Depende de los params, no del VM: de una nota a otra la ruta es la misma, así que esta
  // vista no se remonta y un efecto con `[vm]` no se volvía a disparar. Dependencias
  // primitivas para no rearmar un objeto en cada render.
  useEffect(() => {
    void vm.irA({ year, month, urlKey });
  }, [vm, year, month, urlKey]);
  useSincronizarPagina(state.data);
  useSenalDePagina('NOTA', state.data?.nota.id, state.data?.nota.id);
  // El aviso de lectura que alimenta "Más leídas". Va sobre el id de la nota y no sobre la ruta: al
  // navegar de una nota a otra esta vista no se remonta, y el hook tiene que volver a contar.
  // Un solo camino para todo lo que produce leer una nota, incluida la lectura misma: va por
  // `/senales` y termina en el mismo `LecturasService` que alimenta "Más leídas".
  useSenalesDeNota(state.data?.nota.id);
  // Al navegar entre notas no corre ningún loader, así que el `<head>` lo reaplica la vista
  // con el mismo armador que usa el server. Memorizado: `useHead` depende de la identidad
  // del descriptor y si no, correría el efecto en cada render.
  useHead(
    useMemo(
      () => (state.data ? headDeNota(state.data.nota, sitio) : null),
      [state.data, sitio],
    ),
  );

  // El loader resolvió 404 (nota archivada o inexistente): el server ya respondió con
  // status 404 y estos son los datos de la página de error. Solo mientras siga en esa
  // dirección: la página de 404 ofrece notas como salida, y si se clickea una hay que
  // mostrarla, no quedarse en el error.
  if (direccionDelSsr === `${year}/${month}/${urlKey}` && initial !== null && !esNota) {
    return <NotFoundView data={initial} />;
  }
  if (state.noEncontrada) return <NotFoundView />;

  const data = state.data;
  if (!data) {
    return (
      <Box sx={{ maxWidth: 880, mx: 'auto', py: 2 }}>
        {state.error ? (
          <Alert severity="error">{state.error}</Alert>
        ) : (
          <>
            <Skeleton height={56} />
            <Skeleton height={56} width="70%" />
            <Skeleton variant="rectangular" height={320} sx={{ mt: 2 }} />
          </>
        )}
      </Box>
    );
  }

  const { nota, masDe, ultimaHora, ads } = data;
  const categoria = nota.categories[0];
  // El origen sale del request que sirvió la página, no de `Tenant.domain`: ese es opcional
  // (y en la demo es null), no conoce el esquema y no vale detrás de un túnel. Compartir un
  // path relativo es mandar un link roto a WhatsApp y a Facebook.
  const urlAbsoluta = `${sitio.url}${nota.path}`;

  return (
    <>
      <EspacioPublicitario ubicacion="HEADER" ad={ads.HEADER} />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 300px' },
          gap: { xs: 0, lg: 4 },
          alignItems: 'stretch',
          mt: 1,
        }}
      >
        <Box component="article" sx={{ maxWidth: 880, mx: 'auto', width: '100%', minWidth: 0 }}>
          {/* Migas: portada y categoría, con área táctil de 44 px. */}
          <Box component="nav" aria-label="Estás en" sx={{ display: 'flex', gap: 1.5 }}>
            <MuiLink
              component={RouterLink}
              to="/"
              sx={{ display: 'flex', alignItems: 'center', minHeight: 44, fontSize: 13 }}
            >
              Portada
            </MuiLink>
            {categoria && (
              <MuiLink
                component={RouterLink}
                to={`/${categoria.urlKey}`}
                sx={{ display: 'flex', alignItems: 'center', minHeight: 44, fontSize: 13 }}
              >
                {categoria.name}
              </MuiLink>
            )}
          </Box>

          {categoria && (
            <Typography
              variant="volanta"
              component="p"
              sx={{ color: 'var(--color-cliente)', mt: 1 }}
            >
              {categoria.name}
            </Typography>
          )}

          <Typography variant="tituloNota" component="h1" sx={{ mt: 0.5 }}>
            {nota.title}
          </Typography>

          {nota.excerpt && (
            <Typography variant="bajada" component="p" sx={{ mt: 1.5 }}>
              {nota.excerpt}
            </Typography>
          )}

          {/* Firma y fecha entre reglas de 1 px, compartir a la derecha. */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
              borderTop: `1px solid ${neutral[300]}`,
              borderBottom: `1px solid ${neutral[300]}`,
              py: 0.5,
              mt: 2,
            }}
          >
            <Typography variant="firma" component="p" sx={{ color: neutral[700] }}>
              Por {nota.author} · {formatearFechaYHora(nota.publishedAt)}
            </Typography>
            <BotonesCompartir url={urlAbsoluta} titulo={nota.title} />
          </Box>

          <Box sx={{ mt: 2 }}>
            <ImagenNota imagen={nota.cover} ratio="16:9" sizes={SIZES.cuerpo} prioritaria />
            {nota.cover?.alt && (
              <Typography variant="epigrafe" component="p" sx={{ color: neutral[700], mt: 0.5 }}>
                {nota.cover.alt}
              </Typography>
            )}
          </Box>

          <RendererCuerpo body={nota.body} adEntrenota={ads.IN_ARTICLE} />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              borderTop: `1px solid ${neutral[300]}`,
              mt: 2,
            }}
          >
            <BotonesCompartir url={urlAbsoluta} titulo={nota.title} />
          </Box>

          {masDe && <MasDeCategoria category={masDe.category} notas={masDe.notas} />}
        </Box>

        <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
          <TiraLateral
            notas={ultimaHora}
            titulo={data.shell.ultimaHoraNombre}
            verTodo={`/${SECCION_KIND.ultimaHora}`}
            ad={ads.SIDEBAR}
          />
        </Box>
      </Box>
    </>
  );
}
